<template>
  <div class="company_dashboard-container company_dashboard-main company_dashboard-page--glass glass-tab-page">
    <BrucdcumHeader :breadcrumb="['Dashboard', 'Dashboard']" />

    <div class="glass-tab-view glass-tab-view--flush company_dashboard-glass-shell">
      <div class="glass-tab-view__tabs">
        <TabNavigation
          :tabs="dashboardTabs"
          v-model="activeTab"
        />
      </div>

      <div class="glass-tab-view__content company_dashboard-page-stack">
        <!-- Company Details tab -->
        <template v-if="activeTab === 'companyDetails'">
          <div class="company_dashboard-header-block">
            <div class="company_dashboard-header-row">
              <div class="company_dashboard-header-row__text">
                <h2 class="company_dashboard-title">Company Details</h2>
                <p class="company_dashboard-subtitle">
                  Overview of banking, sales, expenses, and key business metrics
                </p>
              </div>
              <div class="company_dashboard-header-actions">
                <SecondaryButton
                  label="Configure"
                  icon="bi bi-gear"
                  @click="openConfigureModal"
                />
                <PrimaryButton
                  label="Import From CSV"
                  icon="bi bi-file-earmark-arrow-up"
                  @click="goToImportCSV"
                />
              </div>
            </div>
          </div>

          <div class="company_dashboard-grid company_dashboard-grid--glass">
            <div
              v-for="(widget, index) in companyDetailsWidgets"
              :key="widget.key"
              :class="[
                'company_dashboard-widget',
                'company_dashboard-widget--glass',
                'glass-tab-panel--nested',
                widget.wide ? 'company_dashboard-widget-wide' : '',
              ]"
              :style="{ animationDelay: `${0.08 + index * 0.035}s` }"
            >
              <div class="company_dashboard-widget-header">
                <span class="company_dashboard-widget-title">{{ widget.label }}</span>
                <button
                  type="button"
                  class="company_dashboard-widget-close-btn"
                  title="Close widget"
                  aria-label="Close widget"
                  @click="closeWidget(widget.key)"
                >
                  <i class="bi bi-x-lg" aria-hidden="true"></i>
                </button>
              </div>
              <div class="company_dashboard-widget-content">
                <component
                  :is="widget.component"
                  :companyId="companyId"
                  :key="widget.key"
                />
              </div>
            </div>
          </div>
        </template>

        <!-- Quick Access tab -->
        <template v-else-if="activeTab === 'quickAccess'">
          <div class="company_dashboard-header-block">
            <div class="company_dashboard-header-row">
              <div class="company_dashboard-header-row__text">
                <h2 class="company_dashboard-title">Quick Access</h2>
              </div>
            </div>
          </div>

          <div class="company_dashboard-quick-access glass-tab-panel--nested">
            <QuickLinksWidget :companyId="companyId" />
          </div>
        </template>
      </div>
    </div>

    <Teleport to="body">
      <Transition name="company-dashboard-drawer-overlay">
        <div
          v-if="showConfigureModal"
          class="company_dashboard-drawer-overlay"
          role="presentation"
          @click.self="closeConfigureModal"
        >
          <Transition name="company-dashboard-drawer-panel" appear>
            <aside
              v-if="showConfigureModal"
              class="company_dashboard-drawer company_dashboard-drawer--glass"
              role="dialog"
              aria-modal="true"
              aria-labelledby="company-dashboard-drawer-title"
              @click.stop
            >
              <header class="company_dashboard-drawer-header">
                <h3 id="company-dashboard-drawer-title">Configure Widgets</h3>
                <button
                  type="button"
                  class="company_dashboard-drawer-close-btn"
                  aria-label="Close"
                  @click="closeConfigureModal"
                >
                  <i class="bi bi-x-lg" aria-hidden="true"></i>
                </button>
              </header>
              <div class="company_dashboard-drawer-body">
                <label class="company_dashboard-drawer-select-all-row">
                  <input
                    type="checkbox"
                    :checked="allWidgetsSelected"
                    @change="toggleSelectAll"
                  />
                  <span>{{ allWidgetsSelected ? 'Deselect All' : 'Select All' }}</span>
                </label>
                <div class="company_dashboard-drawer-divider"></div>
                <label
                  v-for="option in widgetOptions"
                  :key="option.key"
                  class="company_dashboard-drawer-checkbox-row"
                  :for="`widget-${option.key}`"
                >
                  <input
                    type="checkbox"
                    :id="`widget-${option.key}`"
                    v-model="tempSelectedWidgets"
                    :value="option.key"
                  />
                  <span>{{ option.label }}</span>
                </label>
              </div>
              <footer class="company_dashboard-drawer-footer">
                <SecondaryButton label="Cancel" @click="closeConfigureModal" />
                <PrimaryButton label="OK" icon="bi bi-check-lg" @click="applyConfigure" />
              </footer>
            </aside>
          </Transition>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
import '../styles/CompanyDashboard.css';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import TabNavigation from '@/components/TabNavigation.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';
import logger from '@/utils/logger';

import BankingWidget from './widgets/BankingWidget.vue';
import ExpensesWidget from './widgets/ExpensesWidget.vue';
import WhoIOweWidget from './widgets/WhoIOweWidget.vue';
import RecentTransactionsWidget from './widgets/RecentTransactionsWidget.vue';
import AccountBalancesWidget from './widgets/AccountBalancesWidget.vue';
import TopCustomersBySalesWidget from './widgets/TopCustomersBySalesWidget.vue';
import TopItemsBySalesWidget from './widgets/TopItemsBySalesWidget.vue';
import YearOverYearIncomeWidget from './widgets/YearOverYearIncomeWidget.vue';
import BankAccountWidget from './widgets/BankAccountWidget.vue';
import MoneyComingInWidget from './widgets/MoneyComingInWidget.vue';
import MoneyGoingOutWidget from './widgets/MoneyGoingOutWidget.vue';
import WhoOwesMeWidget from './widgets/WhoOwesMeWidget.vue';
import QuickLinksWidget from './widgets/QuickLinksWidget.vue';
import MessagesTasksWidget from './widgets/MessagesTasksWidget.vue';
import TopVendorsByExpensesWidget from './widgets/TopVendorsByExpensesWidget.vue';
import IncomeExpenseAccountWidget from './widgets/IncomeExpenseAccountWidget.vue';
import IncomesWidget from './widgets/IncomesWidget.vue';
import YearOverYearExpenseWidget from './widgets/YearOverYearExpenseWidget.vue';
import ProfitLossWidget from './widgets/ProfitLossWidget.vue';
import InvoicesStatusWidget from './widgets/InvoicesStatusWidget.vue';
import CashFlowWidget from './widgets/CashFlowWidget.vue';
import UnpaidBillsWidget from './widgets/UnpaidBillsWidget.vue';
import BillsStatusWidget from './widgets/BillsStatusWidget.vue';

export default {
  name: 'CompanyDashboard',
  components: {
    BrucdcumHeader,
    TabNavigation,
    PrimaryButton,
    SecondaryButton,
    BankingWidget,
    ExpensesWidget,
    WhoIOweWidget,
    RecentTransactionsWidget,
    AccountBalancesWidget,
    TopCustomersBySalesWidget,
    TopItemsBySalesWidget,
    YearOverYearIncomeWidget,
    BankAccountWidget,
    MoneyComingInWidget,
    MoneyGoingOutWidget,
    WhoOwesMeWidget,
    QuickLinksWidget,
    MessagesTasksWidget,
    TopVendorsByExpensesWidget,
    IncomeExpenseAccountWidget,
    IncomesWidget,
    YearOverYearExpenseWidget,
    ProfitLossWidget,
    InvoicesStatusWidget,
    CashFlowWidget,
    UnpaidBillsWidget,
    BillsStatusWidget,
  },
  data() {
    const widgetOptions = [
      { key: 'banking', label: 'Banking', component: 'BankingWidget' },
      { key: 'expenses', label: 'Expenses', component: 'ExpensesWidget' },
      { key: 'whoIOwe', label: 'Who I Owe', component: 'WhoIOweWidget' },
      { key: 'recentTransactions', label: 'Recent Transactions', component: 'RecentTransactionsWidget' },
      { key: 'accountBalances', label: 'Account Balances', component: 'AccountBalancesWidget' },
      { key: 'topCustomersBySales', label: 'Top Customers By Sales', component: 'TopCustomersBySalesWidget' },
      { key: 'topItemsBySales', label: 'Top Items By Sales', component: 'TopItemsBySalesWidget' },
      { key: 'yearOverYearIncome', label: 'Year Over Year Income', component: 'YearOverYearIncomeWidget' },
      { key: 'bankAccount', label: 'Bank Account', component: 'BankAccountWidget' },
      { key: 'moneyComingIn', label: 'Money Coming In', component: 'MoneyComingInWidget' },
      { key: 'moneyGoingOut', label: 'Money Going Out', component: 'MoneyGoingOutWidget' },
      { key: 'whoOwesMe', label: 'Who Owes Me', component: 'WhoOwesMeWidget' },
      { key: 'messagesTasks', label: 'Messages And Tasks', component: 'MessagesTasksWidget' },
      { key: 'topVendorsByExpenses', label: 'Top Vendors By Expenses', component: 'TopVendorsByExpensesWidget' },
      { key: 'incomeExpenseAccount', label: 'Income and Expense Account', component: 'IncomeExpenseAccountWidget' },
      { key: 'incomes', label: 'Incomes', component: 'IncomesWidget' },
      { key: 'yearOverYearExpense', label: 'Year Over Year Expense', component: 'YearOverYearExpenseWidget' },
      { key: 'profitLoss', label: 'Profit & Loss', component: 'ProfitLossWidget' },
      { key: 'invoicesStatus', label: 'Invoices Status', component: 'InvoicesStatusWidget' },
      { key: 'cashFlow', label: 'Cash Flow', component: 'CashFlowWidget' },
      { key: 'unpaidBills', label: 'Unpaid Bills', component: 'UnpaidBillsWidget' },
      { key: 'billsStatus', label: 'Bills Status', component: 'BillsStatusWidget' },
    ];

    const defaultWidgetOrder = [
      'profitLoss', 'cashFlow',
      'moneyComingIn', 'moneyGoingOut', 'whoOwesMe', 'whoIOwe',
      'invoicesStatus', 'billsStatus',
      'topCustomersBySales', 'topVendorsByExpenses',
      'incomes', 'expenses',
      'yearOverYearExpense', 'yearOverYearIncome',
      'incomeExpenseAccount', 'recentTransactions',
      'accountBalances', 'topItemsBySales',
      'unpaidBills',
      'messagesTasks',
      'banking', 'bankAccount',
    ];

    const allWidgetKeys = widgetOptions.map((w) => w.key);
    const missingWidgets = allWidgetKeys.filter((key) => !defaultWidgetOrder.includes(key));
    const completeDefaultOrder = [...defaultWidgetOrder, ...missingWidgets];

    return {
      activeTab: 'quickAccess',
      dashboardTabs: [
        { id: 'quickAccess', name: 'Quick Access' },
        { id: 'companyDetails', name: 'Company Details' },
      ],
      showConfigureModal: false,
      widgetOptions,
      defaultWidgetOrder: completeDefaultOrder,
      selectedWidgets: [...completeDefaultOrder],
      tempSelectedWidgets: [],
    };
  },
  computed: {
    companyId() {
      return this.$route.params.id || this.$route.params.companyId;
    },
    allWidgetsSelected() {
      return this.tempSelectedWidgets.length === this.widgetOptions.length
        && this.widgetOptions.every((option) => this.tempSelectedWidgets.includes(option.key));
    },
    companyDetailsWidgets() {
      const orderMap = new Map();
      this.selectedWidgets.forEach((key, index) => {
        orderMap.set(key, index);
      });

      return this.widgetOptions
        .filter((w) => this.selectedWidgets.includes(w.key))
        .sort((a, b) => {
          const orderA = orderMap.get(a.key) ?? Infinity;
          const orderB = orderMap.get(b.key) ?? Infinity;
          return orderA - orderB;
        })
        .map((w) => ({
          ...w,
          component: this.getComponentByName(w.component),
        }));
    },
  },
  created() {
    this.loadWidgetPreferences();
    this.tempSelectedWidgets = [...this.selectedWidgets];
  },
  methods: {
    getComponentByName(name) {
      const componentMap = {
        BankingWidget,
        ExpensesWidget,
        WhoIOweWidget,
        RecentTransactionsWidget,
        AccountBalancesWidget,
        TopCustomersBySalesWidget,
        TopItemsBySalesWidget,
        YearOverYearIncomeWidget,
        BankAccountWidget,
        MoneyComingInWidget,
        MoneyGoingOutWidget,
        WhoOwesMeWidget,
        QuickLinksWidget,
        MessagesTasksWidget,
        TopVendorsByExpensesWidget,
        IncomeExpenseAccountWidget,
        IncomesWidget,
        YearOverYearExpenseWidget,
        ProfitLossWidget,
        InvoicesStatusWidget,
        CashFlowWidget,
        UnpaidBillsWidget,
        BillsStatusWidget,
      };
      return componentMap[name] || null;
    },
    openConfigureModal() {
      this.tempSelectedWidgets = [...this.selectedWidgets];
      this.showConfigureModal = true;
    },
    closeWidget(widgetKey) {
      this.selectedWidgets = this.selectedWidgets.filter((key) => key !== widgetKey);
      this.saveWidgetPreferences();
    },
    closeConfigureModal() {
      this.showConfigureModal = false;
      this.tempSelectedWidgets = [...this.selectedWidgets];
    },
    toggleSelectAll() {
      if (this.allWidgetsSelected) {
        this.tempSelectedWidgets = [];
      } else {
        this.tempSelectedWidgets = this.widgetOptions.map((option) => option.key);
      }
    },
    applyConfigure() {
      const orderedWidgets = this.defaultWidgetOrder.filter((key) => this.tempSelectedWidgets.includes(key));
      const additionalWidgets = this.tempSelectedWidgets.filter((key) => !this.defaultWidgetOrder.includes(key));
      this.selectedWidgets = [...orderedWidgets, ...additionalWidgets];
      this.saveWidgetPreferences();
      this.showConfigureModal = false;
    },
    saveWidgetPreferences() {
      localStorage.setItem(`dashboard_widgets_${this.companyId}`, JSON.stringify(this.selectedWidgets));
    },
    loadWidgetPreferences() {
      const saved = localStorage.getItem(`dashboard_widgets_${this.companyId}`);
      if (saved) {
        try {
          const savedWidgets = JSON.parse(saved);
          const knownKeys = new Set(this.widgetOptions.map((w) => w.key));
          const validSaved = savedWidgets.filter((key) => knownKeys.has(key));
          const orderedWidgets = this.defaultWidgetOrder.filter((key) => validSaved.includes(key));
          const additionalWidgets = validSaved.filter((key) => !this.defaultWidgetOrder.includes(key));
          this.selectedWidgets = [...orderedWidgets, ...additionalWidgets];
        } catch (e) {
          logger.error('Error loading widget preferences:', e);
          this.selectedWidgets = [...this.defaultWidgetOrder];
        }
      } else {
        this.selectedWidgets = [...this.defaultWidgetOrder];
      }
    },
    goToImportCSV() {
      const companyId = this.companyId;
      this.$router.push(`/company/${companyId}/dashboard/importcsv`);
    },
  },
};
</script>
